import React, { useState, useRef, useEffect } from "react";
import { Input } from "./Input";
import { Select } from "./Select";
import { geoNames } from "../../utils/countriesGeonames";
import { handleKeyPress } from "../../utils/Helper";
import { useAddBillingAddressMutation } from "../../store/Billing/billingApiSlice";
import { ReactComponent as Spinner } from "../../assets/icons/spinner.svg";
import { useSelector } from "react-redux";
import { getCountryCode, getStatesByCountryCode, extractCountryCodeAndPhoneCode } from "../../utils/countriesState";
import PhoneNumberInput from "./PhoneNumberInput";
import { getCountryCallingCode, isPossiblePhoneNumber, parsePhoneNumber } from "react-phone-number-input";

// Validation utilities
const sanitizeInput = (input) => {
  if (!input) return '';
  // Remove any HTML tags and special characters that could be used for XSS/SQL injection
  return String(input)
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/[&]/g, '&amp;')
    .replace(/['"]/g, '') // Remove quotes
    .trim();
};

const validateAddressLine = (value, fieldName) => {
  if (!value || value.trim() === '') {
    return `${fieldName} is required`;
  }
  
  const sanitized = sanitizeInput(value);
  if (sanitized.length < 2) {
    return `${fieldName} must be at least 2 characters long`;
  }
  
  if (sanitized.length > 200) {
    return `${fieldName} must not exceed 200 characters`;
  }
  
  // Check for SQL injection patterns
  const sqlPattern = /(\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b|\bUNION\b|\b--\b|;|\bOR\b|\bAND\b)/i;
  if (sqlPattern.test(sanitized)) {
    return `Invalid characters in ${fieldName}`;
  }
  
  // Check for allowed characters (alphanumeric, spaces, hyphens, periods, commas, #, /)
  const allowedPattern = /^[a-zA-Z0-9\s\-.,#/]+$/;
  if (!allowedPattern.test(sanitized)) {
    return `${fieldName} contains invalid characters`;
  }
  
  return null;
};

const validatePostalCode = (value, countryCode) => {
  if (!value || value.trim() === '') {
    return null; // Postal code can be optional
  }
  
  const sanitized = sanitizeInput(value);
  
  // Country-specific postal code patterns
  const patterns = {
    'AE': /^[0-9]{3,5}$/, // UAE
    'US': /^[0-9]{5}(-[0-9]{4})?$/, // USA
    'GB': /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i, // UK
    'CA': /^[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]$/i, // Canada
    'AU': /^[0-9]{4}$/, // Australia
    'IN': /^[0-9]{6}$/, // India
    'DE': /^[0-9]{5}$/, // Germany
    'FR': /^[0-9]{5}$/, // France
    'IT': /^[0-9]{5}$/, // Italy
    'ES': /^[0-9]{5}$/, // Spain
    'JP': /^[0-9]{3}-[0-9]{4}$/, // Japan
    'BR': /^[0-9]{5}-[0-9]{3}$/, // Brazil
  };
  
  const pattern = patterns[countryCode];
  if (pattern && !pattern.test(sanitized)) {
    return `Invalid postal code format for selected country`;
  }
  
  if (sanitized.length > 20) {
    return 'Postal code must not exceed 20 characters';
  }
  
  return null;
};

const validateCompanyName = (value) => {
  if (!value) return null; // Optional field
  
  const sanitized = sanitizeInput(value);
  
  if (sanitized.length < 2) {
    return 'Company name must be at least 2 characters long';
  }
  
  if (sanitized.length > 100) {
    return 'Company name must not exceed 100 characters';
  }
  
  // Allow letters, numbers, spaces, dots, hyphens, and ampersands
  const allowedPattern = /^[a-zA-Z0-9\s\.\-&]+$/;
  if (!allowedPattern.test(sanitized)) {
    return 'Company name can only contain letters, numbers, spaces, dots (.), hyphens (-), and ampersands (&)';
  }
  
  // Check for SQL injection patterns
  const sqlPattern = /(\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b|\bUNION\b|\b--\b|;|\bOR\b|\bAND\b)/i;
  if (sqlPattern.test(sanitized)) {
    return 'Company name contains invalid characters or keywords';
  }
  
  // Additional check: no multiple consecutive spaces
  if (/\s{2,}/.test(sanitized)) {
    return 'Company name should not contain multiple consecutive spaces';
  }
  
  // Check that company name doesn't start or end with special characters
  if (/^[\s\.\-&]|[\s\.\-&]$/.test(sanitized)) {
    return 'Company name should not start or end with spaces, dots, hyphens, or ampersands';
  }
  
  return null;
};

const validateTRNNumber = (value) => {
  if (!value) return null; // Optional field
  
  const sanitized = sanitizeInput(value);
  
  if (sanitized.length > 50) {
    return 'Tax Registration Number must not exceed 50 characters';
  }
  
  // Allow alphanumeric, hyphens, and spaces
  const allowedPattern = /^[a-zA-Z0-9\s\-]+$/;
  if (!allowedPattern.test(sanitized)) {
    return 'Tax Registration Number contains invalid characters';
  }
  
  return null;
};

const validateCity = (value) => {
  if (!value || value.trim() === '') {
    return 'City is required';
  }
  
  const sanitized = sanitizeInput(value);
  
  if (sanitized.length < 2) {
    return 'City must be at least 2 characters long';
  }
  
  if (sanitized.length > 100) {
    return 'City must not exceed 100 characters';
  }
  
  // Allow letters, spaces, hyphens, and periods
  const allowedPattern = /^[a-zA-Z\s\-.'()]+$/;
  if (!allowedPattern.test(sanitized)) {
    return 'City contains invalid characters';
  }
  
  return null;
};

const validateState = (value, statesList) => {
  if (!value || value.trim() === '') {
    return 'State is required';
  }
  
  const sanitized = sanitizeInput(value);
  
  if (statesList && statesList.length > 0) {
    // If states are from dropdown, validate against list
    const isValidState = statesList.some(state => 
      state.name.toLowerCase() === sanitized.toLowerCase()
    );
    if (!isValidState) {
      return 'Please select a valid state from the list';
    }
  } else {
    // Free text state validation
    if (sanitized.length < 2) {
      return 'State must be at least 2 characters long';
    }
    
    if (sanitized.length > 100) {
      return 'State must not exceed 100 characters';
    }
    
    const allowedPattern = /^[a-zA-Z\s\-.'()]+$/;
    if (!allowedPattern.test(sanitized)) {
      return 'State contains invalid characters';
    }
  }
  
  return null;
};

const validateMobileNumber = (mobile, countryCode) => {
  if (!mobile) {
    return 'Mobile Number is required';
  }
  
  if (!isPossiblePhoneNumber(mobile?.toString() ?? "")) {
    return 'Enter a valid mobile number';
  }
  
  try {
    const parsedNumber = parsePhoneNumber(mobile);
    if (parsedNumber && countryCode) {
      const callingCode = parsedNumber.countryCallingCode;
      // Additional validation can be added here if needed
    }
  } catch (error) {
    return 'Enter a valid mobile number';
  }
  
  return null;
};

export const AddBillingAddress = ({ refetch, handleOpenAddBillingAddressPopup, setHasAddressDataChanged,
  hasAddressDataChanged }) => {
  const houseNoRef = useRef();
  const companynameRef = useRef();
  const trnnoRef = useRef();
  const localityRef = useRef();
  const areaRef = useRef();
  const postalCodeRef = useRef();
  const cityRef = useRef();
  const [mobile, setMobile] = useState('')
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [countries, setCountries] = useState(getCountryCode());
  const [compContryCode, setCompContryCode] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [countryCodes, setCountryCodes] = useState(extractCountryCodeAndPhoneCode());
  const { userInfo, token } = useSelector((state) => state.auth);
  const [touched, setTouched] = useState({});

  const [addBillingAddress, { isLoading, isSuccess }] =
    useAddBillingAddressMutation();

  useEffect(() => {
    if (mobile) {
      const countryCode = parsePhoneNumber(mobile);      
      countryCode?.countryCallingCode && setCountryCode("+" + countryCode?.countryCallingCode);
    }
  }, [mobile]);

  const fetchStates = async (code) => {
    try {
      setStates(await getStatesByCountryCode(code));
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleCountryChange = async (e) => {
    const selectedCountryValue = e?.target?.value;
    setSelectedCountry(selectedCountryValue);
    setSelectedState("");
    setSelectedCity("");
    // Clear errors for country-dependent fields
    setErrors(prev => ({ ...prev, state: null, postalCode: null }));

    const selectedCountryData = countries.find(
      (country) => country?.countryCode === selectedCountryValue
    );
    if (selectedCountryData) {
      setCompContryCode(selectedCountryData?.countryCode);
      setStates([]);
      setTimeout(async () => {
        const fetchedStates = await getStatesByCountryCode(selectedCountryData?.countryCode);
        setStates(fetchedStates);
      }, 0);
    }
  };

  const handleStateChange = (e) => {
    const selectedStateValue = e.target.value;
    const sanitizedState = sanitizeInput(selectedStateValue);
    setSelectedState(sanitizedState);
    setSelectedCity("");
    setCities([]);
    
    // Validate state
    const stateError = validateState(sanitizedState, states);
    if (stateError) {
      setErrors(prev => ({ ...prev, state: stateError }));
    } else {
      setErrors(prev => ({ ...prev, state: null }));
    }
  };

  const handleCityChange = (e) => {
    const selectedCityValue = e.target.value;
    const sanitizedCity = sanitizeInput(selectedCityValue);
    setSelectedCity(sanitizedCity);
    
    // Validate city
    const cityError = validateCity(sanitizedCity);
    if (cityError) {
      setErrors(prev => ({ ...prev, city: cityError }));
    } else {
      setErrors(prev => ({ ...prev, city: null }));
    }
  };

  const handleFieldBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const validateAllFields = () => {
    const errors = {};
    
    // Address Line 1 (House No)
    const houseNoError = validateAddressLine(houseNoRef.current?.value || '', 'Address Line 1');
    if (houseNoError) errors.houseNo = houseNoError;
    
    // Address Line 2 (Locality) - Optional
    if (localityRef.current?.value) {
      const localityError = validateAddressLine(localityRef.current.value, 'Address Line 2');
      if (localityError) errors.locality = localityError;
    }
    
    // Area/Landmark - Optional
    if (areaRef.current?.value) {
      const areaError = validateAddressLine(areaRef.current.value, 'Landmark');
      if (areaError) errors.area = areaError;
    }
    
    // Company Name - Optional
    const companyError = validateCompanyName(companynameRef.current?.value || '');
    if (companyError) errors.companyName = companyError;
    
    // TRN Number - Optional
    const trnError = validateTRNNumber(trnnoRef.current?.value || '');
    if (trnError) errors.trnNo = trnError;
    
    // City
    const cityError = validateCity(cityRef.current?.value || '');
    if (cityError) errors.city = cityError;
    
    // Mobile
    const mobileError = validateMobileNumber(mobile, countryCode);
    if (mobileError) errors.mobile = mobileError;
    
    // State
    const stateError = validateState(selectedState, states);
    if (stateError) errors.state = stateError;
    
    // Country
    if (!selectedCountry || selectedCountry.trim() === '') {
      errors.country = 'Country is required.';
    }
    
    // Postal Code
    const postalError = validatePostalCode(postalCodeRef.current?.value || '', compContryCode);
    if (postalError) errors.postalCode = postalError;
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allFields = ['houseNo', 'city', 'state', 'country', 'mobile', 'locality', 'area', 'postalCode'];
    const touchedFields = {};
    allFields.forEach(field => { touchedFields[field] = true; });
    setTouched(touchedFields);
    
    // Validate all fields
    const validationErrors = validateAllFields();
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length === 0) {
      try {
        // Sanitize all inputs before sending to API
        const sanitizedData = {
          address_line_1: sanitizeInput(houseNoRef.current.value),
          address_line_2: sanitizeInput(localityRef.current?.value || ''),
          postal_code: sanitizeInput(postalCodeRef.current?.value || ''),
          companyName: sanitizeInput(companynameRef.current?.value || ''),
          trnNo: sanitizeInput(trnnoRef.current?.value || ''),
          area: sanitizeInput(areaRef.current?.value || ''),
          country_code: countryCode,
          mobile: mobile, // Phone number is already validated by library
          city: sanitizeInput(cityRef.current.value),
          state: sanitizeInput(selectedState),
          country: compContryCode,
          type: "individual",
          user_id: userInfo?.individual[0]?.individualID,
        };
        
        // Additional SQL injection prevention - encode special characters
        const encodeForSQL = (obj) => {
          const encoded = {};
          for (const [key, value] of Object.entries(obj)) {
            if (typeof value === 'string') {
              // Replace characters that could be used in SQL injection
              encoded[key] = value
                .replace(/\\/g, '\\\\')
                .replace(/'/g, "''")
                .replace(/\0/g, '\\0');
            } else {
              encoded[key] = value;
            }
          }
          return encoded;
        };
        
        const finalData = encodeForSQL(sanitizedData);
        
        let addBilling = await addBillingAddress({
          data: finalData,
          token: token
        }
        );
        // The mutation isn't unwrapped, so a failed request resolves to
        // { error } rather than throwing — surface it instead of silently
        // closing the modal (otherwise a backend 4xx looks like success).
        if (addBilling?.error) {
          const detail = addBilling.error?.data;
          setApiError(
            detail ? Object.values(detail)[0] : "An error occurred. Please try again later."
          );
        } else if (addBilling?.data) {
          refetch()
          setHasAddressDataChanged(!hasAddressDataChanged);
          handleOpenAddBillingAddressPopup();
        }
      } catch (error) {
        setApiError(
          error?.data
            ? Object.values(error?.data)[0]
            : "An error occurred. Please try again later."
        );
      }
    }
  };

  return (
    <section className="relative px-4 sm:px-0 lg:py-0 overflow-hidden">
      <div className="flex -mx-4 xl:items-center items-center max-h-screen">
        <div className="flex-flex1 w-full lg:w-1/2 xl:w-3/5 px-4 order-last lg:order-first">
          <div style={{ width: "730px" }} className="mx-auto ">
            <h3 className="text-f24 text-primary font-bold mb-12 text-left">
              Add Billing Address
            </h3>
            <form onSubmit={handleSubmit} noValidate>
              <div className="mb-12">
                <div className="flex gap-4 w-full h-[80px]">
                  <Input
                    label="Company Name"
                    id="companyName"
                    inputRef={companynameRef}
                    error={errors.companyName}
                    placeholder="Enter Company Name"
                    onBlur={() => handleFieldBlur('companyName')}
                  />
                  
                  <Input
                    label="Tax Registration Number"
                    id="trnNo"
                    inputRef={trnnoRef}
                    error={errors.trnNo}
                    placeholder="Enter Tax Registration Number"
                    onBlur={() => handleFieldBlur('trnNo')}
                  />
                </div>
                
                <div className="flex gap-4 w-full h-[80px]">
                  <Input
                    label="Address Line 1"
                    id="houseNo"
                    inputRef={houseNoRef}
                    error={errors.houseNo}
                    placeholder="Suite/Building/Floor No."
                    required
                    onBlur={() => handleFieldBlur('houseNo')}
                  />
                  
                  <Input
                    label="Address Line 2"
                    id="locality"
                    inputRef={localityRef}
                    error={errors.locality}
                    placeholder="Street, Area, etc."
                    onBlur={() => handleFieldBlur('locality')}
                  />
                </div>
                
                <div className="flex gap-4 w-full h-[80px]">
                  <div className="w-full">
                    <label
                      htmlFor="country"
                      className="block mb-1 text-primary text-f14 font-semibold text-left"
                    >
                      Country <span className="text-red-500">*</span>
                    </label>
                    <Select
                      id="country"
                      placeholder="Select a country"
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      onBlur={() => handleFieldBlur('country')}
                      className={`w-full border ${errors.country && touched.country ? "border-red-500" : "border-input"
                        } focus:border-primary px-3 py-2 border border-input rounded bg-inputDisable outline-none text-primary`}
                    >
                      <option value="">Select a country</option>
                      {countries?.map((country) => (
                        <option
                          key={country.countryName}
                          value={country.countryCode}
                        >
                          {country.countryName}
                        </option>
                      ))}
                    </Select>
                    {errors.country && touched.country && (
                      <p className="text-red-500 text-sm text-left mt-1">
                        {errors.country}
                      </p>
                    )}
                  </div>
                  
                  {states?.length > 0 ? (
                    <Select
                      label="State"
                      id="state"
                      classes="w-full text-left"
                      value={selectedState}
                      onChange={handleStateChange}
                      onBlur={() => handleFieldBlur('state')}
                      error={errors.state}
                      placeholder="Select a state"
                    >
                      <option value="">Select a state</option>
                      {states?.map((state) => (
                        <option key={state.state_code} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      label="State"
                      id="state"
                      value={selectedState}
                      onInputChange={handleStateChange}
                      onBlur={() => handleFieldBlur('state')}
                      error={errors.state}
                      placeholder="Enter a state"
                      disable={!selectedCountry}
                      className={!selectedCountry ? "cursor-not-allowed" : "default"}
                      required
                    />
                  )}
                </div>
                
                <div className="flex gap-4 w-full h-[80px]">
                  <Input
                    label="City"
                    id="city"
                    inputRef={cityRef}
                    error={errors.city}
                    placeholder="Enter City"
                    required
                    onBlur={() => handleFieldBlur('city')}
                  />
                  
                  <Input
                    label="Landmark"
                    id="area"
                    inputRef={areaRef}
                    error={errors.area}
                    placeholder="Enter Landmark"
                    onBlur={() => handleFieldBlur('area')}
                  />
                </div>
                
                <div className="flex gap-4 w-full h-[80px]">
                  <Input
                    label="Postal Code"
                    id="postalCode"
                    inputRef={postalCodeRef}
                    error={errors.postalCode}
                    placeholder="Enter Postal Code"
                    onBlur={() => handleFieldBlur('postalCode')}
                  />
                  
                  <div className="flex items-start flex-col w-full">
                    <div className="flex items-start flex-col w-full gap-1">
                      <label className="text-primary text-f14 font-semibold">
                        Mobile Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative w-full">
                        <PhoneNumberInput
                          id="mobile"
                          phone={mobile}
                          border={true}
                          setPhone={setMobile}
                          verifyPhoneNumber={false}
                          classes={'addBackground'}
                          onBlur={() => handleFieldBlur('mobile')}
                        />
                        {errors.mobile && touched.mobile && (
                          <p className="text-red-500 text-sm text-left mt-1">{errors.mobile}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {apiError && (
                <div className="text-left text-red-500 mb-5">{apiError}</div>
              )}
              
              <div className="flex justify-between gap-2">
                <button
                  className={`relative group w-full py-3 px-5 text-center text-sm font-semibold text-white bg-primary rounded-md inline-flex items-center justify-center gap-4 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  type="submit"
                  disabled={isLoading}
                >
                  <span className="relative">
                    {isLoading ? <Spinner /> : "Save Changes"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};